import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Settings as SettingsIcon,
  CheckCircle2,
  CalendarPlus,
  UtensilsCrossed as RestaurantIcon,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { Calendar, dateFnsLocalizer, type View, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, startOfDay, endOfDay, startOfMonth, endOfMonth, endOfWeek } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { calendarApi, calendarSourcesApi, calendarConnectApi, driversApi, tenantAdminApi, dailyFocusBlocksApi, calendarConflictsApi } from '../api/services';
import type { CalendarEvent, CalendarSource, AnnualStrategicDriver, RestaurantResult, DailyFocusBlock, CalendarConflict } from '../types/models';
import RestaurantSearchDialog from '../components/RestaurantSearchDialog';
import ConnectCalendarCard from '../components/ConnectCalendarCard';
import CalendarSourcesCard from '../components/CalendarSourcesCard';
import CalendarEventDetail from '../components/CalendarEventDetail';
import { getFYWeek, getFYLabel, getFYQuarter } from '../utils/financialYear';
import { colourForDriverId, mutedColourForDriverId, calendarCalm } from '../theme/tokens';
import { calculateFocusCoverage, minutesToHours } from '../utils/focusCoverage';
import HelpPanel from '../components/HelpPanel';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// CalendarPage — heaviest single page in the SPA at ~920 lines. This pass
// migrates the always-visible chrome to Tailwind + shadcn:
//   • Header bar (title, date nav, view picker, action buttons, settings)
//   • Help panel + weekly focus-coverage strip
//   • Banner alerts (connection / sync / create-event feedback)
//   • Calendar wrapper around react-big-calendar
//
// react-big-calendar itself keeps its own CSS — it ships its own styling
// surface that's hard to substitute. The Settings Drawer and the
// Create-Event Dialog stay MUI for the next follow-up commit; they need
// Sheet / Dialog / Select primitives we haven't added yet.
// ---------------------------------------------------------------------------

// date-fns localizer setup
const locales = { 'en-GB': enGB };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Mapped event type for react-big-calendar
interface CalendarBigEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: CalendarEvent;
}

// Compute the visible range for a given date + view.
// WEEK + DAY views pad an extra week back so the load-trending badge has
// last week's events to compare against without a second API round-trip.
// MONTH already covers ~5 weeks so no padding needed.
function getVisibleRange(date: Date, view: View): { from: Date; to: Date } {
  switch (view) {
    case Views.DAY:
      return {
        from: startOfWeek(subWeeks(date, 1), { weekStartsOn: 1 }),
        to: endOfDay(date),
      };
    case Views.WEEK:
      return {
        from: startOfWeek(subWeeks(date, 1), { weekStartsOn: 1 }),
        to: endOfWeek(date, { weekStartsOn: 1 }),
      };
    case Views.MONTH: {
      const ms = startOfMonth(date);
      const me = endOfMonth(date);
      return {
        from: startOfWeek(ms, { weekStartsOn: 1 }),
        to: endOfWeek(me, { weekStartsOn: 1 }),
      };
    }
    default:
      return { from: startOfDay(date), to: endOfDay(date) };
  }
}

function formatRangeLabel(date: Date, view: View): string {
  switch (view) {
    case Views.DAY:
      return format(date, 'EEEE, d MMMM yyyy');
    case Views.WEEK: {
      const ws = startOfWeek(date, { weekStartsOn: 1 });
      const we = endOfWeek(date, { weekStartsOn: 1 });
      if (ws.getMonth() === we.getMonth()) {
        return `${format(ws, 'd')} - ${format(we, 'd MMMM yyyy')}`;
      }
      if (ws.getFullYear() === we.getFullYear()) {
        return `${format(ws, 'd MMM')} - ${format(we, 'd MMM yyyy')}`;
      }
      return `${format(ws, 'd MMM yyyy')} - ${format(we, 'd MMM yyyy')}`;
    }
    case Views.MONTH:
      return format(date, 'MMMM yyyy');
    default:
      return format(date, 'd MMMM yyyy');
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
// Event tile colours. CSS variables would need getComputedStyle at render
// time, so we hardcode the dark-mode brand hex values from theme/tokens.ts.
// react-big-calendar's `.rbc-event` overrides keep selection/hover treatment
// consistent with the rest of the surface.
const EVENT_COLOR = {
  primary: '#1A6CF0',
  error: '#D32F2F',
  warning: '#ED6C02',
  success: '#2E7D32',
} as const;

// Pick a readable foreground for any solid tile fill. Light driver colours
// (teal/straw/lavender/mint) need dark ink; dark fills (blue/red) need white.
function tileInk(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#10151C' : '#ffffff';
}

// Calendar colour mode — Categorical (per-driver bright fills, today's
// behaviour), Calm (one neutral tile + muted driver edge/dot, default) or
// Minimal (neutral tiles, dot only, clash is the one vivid accent).
type CalColourMode = 'categorical' | 'calm' | 'minimal';

// Strip a leading 🎯 from a driver title — in Calm/Minimal the dot replaces it.
function stripTargetGlyph(title: string): string {
  return title.replace(/^\s*🎯\s*/, '');
}

export default function CalendarPage() {
  // Data state
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [, setLoading] = useState(false);
  const [activeDrivers, setActiveDrivers] = useState<AnnualStrategicDriver[]>([]);

  // Calendar navigation
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentView, setCurrentView] = useState<View>(Views.WEEK);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sync / connection
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [connectionBanner, setConnectionBanner] = useState<string | null>(null);

  // Create Event state
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [oauthSources, setOauthSources] = useState<CalendarSource[]>([]);
  // True once the sources fetch has resolved — gates the "no calendar
  // connected" empty state so it can't flash during initial load.
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [createEventData, setCreateEventData] = useState({
    title: '',
    startTime: '',
    endTime: '',
    location: '',
    description: '',
    eventType: 'Meeting',
    attendees: '',
    sourceId: '',
  });
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [createEventMessage, setCreateEventMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Restaurant search state
  const [restaurantDialogOpen, setRestaurantDialogOpen] = useState(false);

  // Event detail state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);

  // Calendar colour mode — defaults to Calm (colour earned by meaning, not
  // spent per-driver). Persisted to localStorage (v1; graduation path is a
  // server-side PlanningPreference so it follows the user across machines).
  const [colourMode, setColourMode] = useState<CalColourMode>(() => {
    const stored = localStorage.getItem('cadence_calendar_colour_mode');
    // Validate — a stale/garbage value would otherwise fall through every
    // mode branch and render borderless (neither calm nor minimal).
    return stored === 'categorical' || stored === 'calm' || stored === 'minimal'
      ? stored
      : 'calm';
  });
  useEffect(() => { localStorage.setItem('cadence_calendar_colour_mode', colourMode); }, [colourMode]);

  // Focus blocks for the visible date range (for conflict highlighting)
  const [focusBlocks, setFocusBlocks] = useState<DailyFocusBlock[]>([]);

  // Calendar conflicts
  const [calendarConflicts, setCalendarConflicts] = useState<CalendarConflict[]>([]);

  // Financial Year state
  const [fyEnabled, setFyEnabled] = useState(false);
  const [fyStartMonth, setFyStartMonth] = useState(1);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadEventsForRange = useCallback(async (from: Date, to: Date) => {
    setLoading(true);
    try {
      const fromStr = format(from, 'yyyy-MM-dd');
      const toStr = format(to, 'yyyy-MM-dd');
      setEvents(await calendarApi.getByRange(fromStr, toStr));
      calendarApi.sync({ from: fromStr, to: toStr })
        .then(() => calendarApi.getByRange(fromStr, toStr).then(setEvents))
        .catch(() => {});
    } catch (err) {
      // silent-by-design: this loader re-runs on every range change and
      // calendar-sync event; a toast would fire repeatedly. Empty calendar
      // already signals load failure visually.
      console.error('Failed to load events', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCurrentRange = useCallback(() => {
    const { from, to } = getVisibleRange(currentDate, currentView);
    loadEventsForRange(from, to);
  }, [currentDate, currentView, loadEventsForRange]);

  useEffect(() => {
    loadCurrentRange();
  }, [loadCurrentRange]);

  useEffect(() => {
    driversApi.getActive().then(setActiveDrivers).catch(() => {});
  }, []);

  useEffect(() => {
    const handleSync = () => loadCurrentRange();
    window.addEventListener('cadence:calendar-synced', handleSync);
    return () => window.removeEventListener('cadence:calendar-synced', handleSync);
  }, [loadCurrentRange]);

  useEffect(() => {
    const { from, to } = getVisibleRange(currentDate, currentView);
    const fromStr = format(from, 'yyyy-MM-dd');
    const toStr = format(to, 'yyyy-MM-dd');
    dailyFocusBlocksApi.getByRange(fromStr, toStr).then(setFocusBlocks).catch(() => {});
  }, [currentDate, currentView]);

  useEffect(() => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    calendarConflictsApi.getByDate(dateStr).then(setCalendarConflicts).catch(() => setCalendarConflicts([]));
  }, [currentDate]);

  useEffect(() => {
    tenantAdminApi.getFinancialYear().then((fy: any) => {
      setFyEnabled(fy.useFinancialYear);
      setFyStartMonth(fy.useFinancialYear ? fy.financialYearStartMonth : 1);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const loadAndSync = async () => {
      try {
        const all = await calendarSourcesApi.getAll();
        const oauth = all.filter((s: CalendarSource) => s.sourceType === 'Microsoft365' || s.sourceType === 'Google');
        setOauthSources(oauth);
        if (oauth.length > 0 && !createEventData.sourceId) {
          setCreateEventData(prev => ({ ...prev, sourceId: oauth[0].id }));
        }
        const connected = oauth.filter((s: CalendarSource) => s.isConnected && !s.needsReconnect);
        for (const source of connected) {
          calendarConnectApi.sync(source.id).catch(() => {});
        }
        calendarApi.sync().catch(() => {});
        setTimeout(() => loadCurrentRange(), 2000);
        setSourcesLoaded(true);
      } catch {
        calendarApi.sync().catch(() => {});
        setTimeout(() => loadCurrentRange(), 2000);
        // Deliberately NOT setting sourcesLoaded on failure — better to show
        // no prompt than a wrong "connect your calendar" over a transient error.
      }
    };
    loadAndSync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected === 'microsoft' || connected === 'google') {
      const label = connected === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      setConnectionBanner(`${label} connected successfully! Your calendar will sync automatically.`);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    // OAuth callback bounces back here with ?error=… when the mailbox-mismatch
    // guard fires (see CalendarConnectController.MicrosoftCallback /
    // GoogleCallback). Surface the message verbatim so users see the real
    // explanation ("already connected to alex@…, to connect alex.personal@…
    // disconnect the existing source first…") instead of the generic 500
    // that hit before commit (this commit).
    const err = params.get('error');
    if (err) {
      setSyncMessage({ type: 'error', text: err });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Event mapping for react-big-calendar
  // -------------------------------------------------------------------------

  const driverMap = useMemo(() => {
    const map = new Map<string, string>();
    activeDrivers.forEach(d => map.set(d.id, d.title));
    return map;
  }, [activeDrivers]);

  const mappedEvents: CalendarBigEvent[] = useMemo(() =>
    events.map(event => ({
      id: event.id,
      title: event.strategicDriverId && driverMap.has(event.strategicDriverId)
        ? `\u{1F3AF} ${event.title}`
        : event.title,
      start: new Date(event.startTime),
      end: new Date(event.endTime),
      allDay: event.isAllDay,
      resource: event,
    })),
    [events, driverMap],
  );

  const fyInfo = useMemo(() => {
    if (!fyEnabled) return null;
    const label = getFYLabel(currentDate, fyStartMonth);
    const quarter = getFYQuarter(currentDate, fyStartMonth);
    const week = getFYWeek(currentDate, fyStartMonth);
    return { label, quarter, week };
  }, [currentDate, fyEnabled, fyStartMonth]);

  // -------------------------------------------------------------------------
  // Navigation handlers
  // -------------------------------------------------------------------------

  const handleNavigateBack = () => {
    setCurrentDate(prev => {
      switch (currentView) {
        case Views.DAY: return subDays(prev, 1);
        case Views.WEEK: return subWeeks(prev, 1);
        case Views.MONTH: return subMonths(prev, 1);
        default: return prev;
      }
    });
  };

  const handleNavigateForward = () => {
    setCurrentDate(prev => {
      switch (currentView) {
        case Views.DAY: return addDays(prev, 1);
        case Views.WEEK: return addWeeks(prev, 1);
        case Views.MONTH: return addMonths(prev, 1);
        default: return prev;
      }
    });
  };

  const handleNavigateToday = () => setCurrentDate(new Date());

  // -------------------------------------------------------------------------
  // react-big-calendar callbacks
  // -------------------------------------------------------------------------

  const handleRangeChange = useCallback((range: Date[] | { start: Date; end: Date }) => {
    if (Array.isArray(range)) {
      if (range.length > 0) {
        const mid = new Date((range[0].getTime() + range[range.length - 1].getTime()) / 2);
        setCurrentDate(mid);
        loadEventsForRange(startOfDay(range[0]), endOfDay(range[range.length - 1]));
      }
    } else {
      const mid = new Date((range.start.getTime() + range.end.getTime()) / 2);
      setCurrentDate(mid);
      loadEventsForRange(range.start, range.end);
    }
  }, [loadEventsForRange]);

  const handleSelectEvent = useCallback((event: CalendarBigEvent) => {
    setSelectedEvent(event.resource);
    setEventDetailOpen(true);
  }, []);

  // Weekly focus-coverage band
  const weeklyCoverage = useMemo(() => {
    const weekStartDate = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEndDate = endOfWeek(currentDate, { weekStartsOn: 1 });
    return calculateFocusCoverage(
      events.filter((e) => {
        const d = new Date(e.startTime);
        return d >= weekStartDate && d <= weekEndDate;
      }),
      focusBlocks.filter((b) => {
        const d = new Date(b.date);
        return d >= weekStartDate && d <= weekEndDate;
      }),
      weekStartDate,
      weekEndDate,
    );
  }, [events, focusBlocks, currentDate]);

  const lastWeekCoverage = useMemo(() => {
    const lastWeekStart = startOfWeek(subWeeks(currentDate, 1), { weekStartsOn: 1 });
    const lastWeekEnd = endOfWeek(subWeeks(currentDate, 1), { weekStartsOn: 1 });
    return calculateFocusCoverage(
      events.filter((e) => {
        const d = new Date(e.startTime);
        return d >= lastWeekStart && d <= lastWeekEnd;
      }),
      focusBlocks.filter((b) => {
        const d = new Date(b.date);
        return d >= lastWeekStart && d <= lastWeekEnd;
      }),
      lastWeekStart,
      lastWeekEnd,
    );
  }, [events, focusBlocks, currentDate]);

  const meetingTrend = useMemo(() => {
    const last = lastWeekCoverage.meetingMinutes;
    if (last < 30) return null;
    const deltaMin = weeklyCoverage.meetingMinutes - last;
    const deltaHrs = Math.round(deltaMin / 60 * 10) / 10;
    if (Math.abs(deltaHrs) < 0.5) return { kind: 'same' as const };
    return {
      kind: deltaHrs > 0 ? ('up' as const) : ('down' as const),
      hours: Math.abs(deltaHrs),
    };
  }, [weeklyCoverage.meetingMinutes, lastWeekCoverage.meetingMinutes]);

  const eventPropGetter = useCallback((event: CalendarBigEvent) => {
    const evt = event.resource as CalendarEvent;
    let backgroundColor: string = EVENT_COLOR.primary;
    let borderColor: string = EVENT_COLOR.primary;
    if (evt.isPriorityMeeting || evt.isPrivate) {
      backgroundColor = EVENT_COLOR.error;
      borderColor = EVENT_COLOR.error;
    } else if (evt.isAllDay) {
      backgroundColor = EVENT_COLOR.warning;
      borderColor = EVENT_COLOR.warning;
    } else if (evt.strategicDriverId) {
      backgroundColor = colourForDriverId(evt.strategicDriverId);
      borderColor = backgroundColor;
    }

    const eventDateStr = format(event.start, 'yyyy-MM-dd');
    const eventStartMin = event.start.getHours() * 60 + event.start.getMinutes();
    const eventEndMin = event.end.getHours() * 60 + event.end.getMinutes();
    const conflictsWithFocus = focusBlocks.some(fb => {
      if (fb.date !== eventDateStr) return false;
      const fbStartMin = parseInt(fb.startTime.split(':')[0]) * 60 + parseInt(fb.startTime.split(':')[1]);
      const fbEndMin = parseInt(fb.endTime.split(':')[0]) * 60 + parseInt(fb.endTime.split(':')[1]);
      return eventStartMin < fbEndMin && eventEndMin > fbStartMin;
    });

    const hasDriver = !!evt.strategicDriverId;
    const hasHardConflict = calendarConflicts.some(
      c => c.severity === 'Hard' && (c.calendarEventId === evt.id || c.conflictingEventId === evt.id)
    );

    if (colourMode === 'categorical') {
      const ink = tileInk(backgroundColor);
      // On a light tile the green driver-link border (#2E7D32) disappears —
      // use a darker green so it still reads against teal/mint/straw fills.
      const driverEdge = ink === '#10151C' ? '#1B7A3E' : EVENT_COLOR.success;
      return {
        style: {
          backgroundColor,
          borderColor,
          borderRadius: 4,
          opacity: 1,
          color: ink,
          border: 'none',
          borderLeft: hasHardConflict
            ? '3px solid #D32F2F'
            : conflictsWithFocus
              ? '3px solid #FF5252'
              : hasDriver
                ? `3px solid ${driverEdge}`
                : 'none',
          borderRight: hasHardConflict ? '3px solid #D32F2F' : 'none',
          fontSize: '0.8rem',
        },
      };
    }

    // Calm / Minimal — one neutral slate fill; colour earned by meaning.
    const isPrivate = evt.isPriorityMeeting || evt.isPrivate;
    const isAllDay = evt.isAllDay;
    const driverHue = evt.strategicDriverId ? mutedColourForDriverId(evt.strategicDriverId) : null;
    const isClash = conflictsWithFocus || hasHardConflict;

    let borderLeft = 'none';
    if (colourMode === 'calm') {
      let edge: string = calendarCalm.edgeMeeting;
      if (driverHue) edge = driverHue;
      if (isAllDay) edge = calendarCalm.edgeAllDay;
      if (isPrivate) edge = calendarCalm.edgePrivate;
      if (isClash) edge = calendarCalm.edgeClash;
      borderLeft = `3px solid ${edge}`;
    } else if (isClash) {
      // minimal — only a clash earns a left edge
      borderLeft = `3px solid ${calendarCalm.edgeClash}`;
    }

    return {
      style: {
        backgroundColor: colourMode === 'minimal' && isClash
          ? 'rgba(255,82,82,0.10)'
          : calendarCalm.tileBgDark,
        color: calendarCalm.tileFgDark,
        border: 'none',
        borderLeft,
        borderRadius: 4,
        opacity: 1,
        fontSize: '0.8rem',
      },
    };
  }, [focusBlocks, calendarConflicts, colourMode]);

  // Custom event content — rbc tiles can't take children from eventPropGetter,
  // so the Calm/Minimal driver/private DOT is rendered here. Categorical keeps
  // the title unchanged (the 🎯 prefix + bright fill already convey the driver).
  // Memoised on colourMode so rbc gets a stable component identity per mode.
  const CalendarEventTile = useMemo(() => {
    return function EventTile({ event, title }: { event: CalendarBigEvent; title: string }) {
      if (colourMode === 'categorical') {
        return <span>{title}</span>;
      }
      const evt = event.resource;
      const isPrivate = evt.isPriorityMeeting || evt.isPrivate;
      const dotColour = evt.strategicDriverId
        ? mutedColourForDriverId(evt.strategicDriverId)
        : isPrivate
          ? calendarCalm.edgePrivate
          : null;
      const label = stripTargetGlyph(title);
      return (
        <span className="inline-flex items-center gap-1">
          {dotColour && (
            <span
              aria-hidden
              style={{
                width: 7, height: 7, borderRadius: '50%',
                backgroundColor: dotColour, flexShrink: 0,
              }}
            />
          )}
          <span className="truncate">{label}</span>
        </span>
      );
    };
  }, [colourMode]);

  // Restaurant select handler
  const handleRestaurantSelect = (restaurant: RestaurantResult) => {
    setCreateEventData(prev => ({
      ...prev,
      title: `Dinner at ${restaurant.name}`,
      location: restaurant.address || restaurant.name,
      description: [
        restaurant.name,
        restaurant.rating ? `Rating: ${restaurant.rating}/5` : null,
        restaurant.address,
        restaurant.googleMapsUrl ? `Google Maps: ${restaurant.googleMapsUrl}` : null,
      ].filter(Boolean).join('\n'),
    }));
    setCreateEventOpen(true);
  };

  const handleCreateEvent = async () => {
    if (!createEventData.title.trim() || !createEventData.startTime || !createEventData.endTime || !createEventData.sourceId) return;
    setCreatingEvent(true);
    setCreateEventMessage(null);
    try {
      await calendarSourcesApi.createEvent(createEventData.sourceId, {
        title: createEventData.title,
        startTime: createEventData.startTime,
        endTime: createEventData.endTime,
        location: createEventData.location || undefined,
        description: createEventData.description || undefined,
        eventType: createEventData.eventType,
        attendees: createEventData.attendees
          ? createEventData.attendees.split(',').map(a => a.trim()).filter(Boolean)
          : undefined,
      });
      setCreateEventOpen(false);
      setCreateEventData({
        title: '',
        startTime: '',
        endTime: '',
        location: '',
        description: '',
        eventType: 'Meeting',
        attendees: '',
        sourceId: oauthSources.length > 0 ? oauthSources[0].id : '',
      });
      setCreateEventMessage({ type: 'success', text: 'Event created successfully. Refreshing calendar...' });
      loadCurrentRange();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create event';
      setCreateEventMessage({ type: 'error', text: typeof msg === 'string' ? msg : 'Failed to create event' });
    } finally {
      setCreatingEvent(false);
    }
  };

  // Stats (for drawer)
  const meetingMinutes = events
    .filter(e => !e.isAllDay)
    .reduce((sum, e) => sum + (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 60000, 0);

  const priorityMeetingCount = events.filter(e => e.isPriorityMeeting || e.isPrivate).length;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="flex h-[calc(100vh-80px)] flex-col">
      {/* Header bar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        {/* Left: Title */}
        <h1 className="mr-2 font-serif text-2xl font-light tracking-tight">Calendar</h1>

        {/* Centre: Date navigation */}
        <div className="flex flex-1 items-center justify-center gap-2">
          <Button size="icon-sm" variant="ghost" onClick={handleNavigateBack} aria-label="Previous">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={handleNavigateToday}>Today</Button>
          <Button size="icon-sm" variant="ghost" onClick={handleNavigateForward} aria-label="Next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="mx-1 whitespace-nowrap text-lg font-medium">
            {formatRangeLabel(currentDate, currentView)}
          </h2>
          {fyInfo && (
            <p className="ml-2 whitespace-nowrap text-sm text-muted-foreground">
              {fyInfo.label} · Q{fyInfo.quarter} · Week {fyInfo.week}
            </p>
          )}
        </div>

        {/* Right: View toggle + actions + settings */}
        <div className="flex items-center gap-2">
          {/* View toggle — three buttons in a bordered pill behaving as a
              radiogroup so screen readers + keyboard users get the same
              semantics MUI's ToggleButtonGroup provided. Arrow keys cycle
              the selection; Home/End jump to the ends. */}
          <div
            role="radiogroup"
            aria-label="Calendar view"
            className="inline-flex rounded-md border border-border bg-card/40 p-0.5"
            onKeyDown={(e) => {
              const order = [Views.DAY, Views.WEEK, Views.MONTH] as const;
              const i = order.indexOf(currentView as typeof order[number]);
              if (i < 0) return;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                setCurrentView(order[(i - 1 + order.length) % order.length]);
              } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                setCurrentView(order[(i + 1) % order.length]);
              } else if (e.key === 'Home') {
                e.preventDefault();
                setCurrentView(order[0]);
              } else if (e.key === 'End') {
                e.preventDefault();
                setCurrentView(order[order.length - 1]);
              }
            }}
          >
            {([Views.DAY, Views.WEEK, Views.MONTH] as const).map((v) => {
              const checked = currentView === v;
              return (
                <Button
                  key={v}
                  role="radio"
                  aria-checked={checked}
                  // Only the checked option is in the tab order; arrow keys
                  // move focus within the group from there.
                  tabIndex={checked ? 0 : -1}
                  size="sm"
                  variant={checked ? 'default' : 'ghost'}
                  onClick={() => setCurrentView(v)}
                  className="h-7 px-3 text-xs capitalize"
                >
                  {v.toLowerCase()}
                </Button>
              );
            })}
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                onClick={() => setCreateEventOpen(true)}
                disabled={oauthSources.length === 0}
                className="gap-1.5"
              >
                <CalendarPlus className="h-4 w-4" />
                Create Event
              </Button>
            </TooltipTrigger>
            {oauthSources.length === 0 && (
              <TooltipContent>
                Connect a Microsoft 365 or Google calendar to create events
              </TooltipContent>
            )}
          </Tooltip>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRestaurantDialogOpen(true)}
            className="gap-1.5"
          >
            <RestaurantIcon className="h-4 w-4" />
            Book Restaurant
          </Button>

          {/* Colour mode — Categorical / Calm / Minimal. Mirrors the view-pill
              radiogroup above (same a11y: arrows cycle, Home/End jump). */}
          <div
            role="radiogroup"
            aria-label="Calendar colour mode"
            className="inline-flex rounded-md border border-border bg-card/40 p-0.5"
            onKeyDown={(e) => {
              const order = ['categorical', 'calm', 'minimal'] as const;
              const i = order.indexOf(colourMode);
              if (i < 0) return;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                setColourMode(order[(i - 1 + order.length) % order.length]);
              } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                setColourMode(order[(i + 1) % order.length]);
              } else if (e.key === 'Home') {
                e.preventDefault();
                setColourMode(order[0]);
              } else if (e.key === 'End') {
                e.preventDefault();
                setColourMode(order[order.length - 1]);
              }
            }}
          >
            {(['categorical', 'calm', 'minimal'] as const).map((m) => {
              const checked = colourMode === m;
              return (
                <Button
                  key={m}
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked ? 0 : -1}
                  size="sm"
                  variant={checked ? 'default' : 'ghost'}
                  onClick={() => setColourMode(m)}
                  className="h-7 px-3 text-xs capitalize"
                >
                  {m}
                </Button>
              );
            })}
          </div>

          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => setDrawerOpen(true)}
            aria-label="Calendar settings"
          >
            <SettingsIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <HelpPanel title="About Your Calendar" storageKey="calendar">
        Calendar syncs from Microsoft 365 or Google. Link events to strategic drivers for time tracking.
      </HelpPanel>

      {/* Weekly focus-coverage band. Baseline is 40h; if the sum of meetings
          and focus tips over 100% the band flips to a warning palette. */}
      <div className="mb-2 rounded-lg border border-border bg-card/60 p-3 backdrop-blur">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
          <p className="min-w-[120px] text-sm font-semibold">This week</p>
          <div className="w-full flex-1">
            <div className="flex h-2.5 overflow-hidden rounded bg-muted/60">
              <div
                className={cn(
                  'transition-all',
                  weeklyCoverage.overloaded ? 'bg-rose-500' : 'bg-primary',
                )}
                style={{ width: `${Math.min(weeklyCoverage.meetingPercent, 100)}%` }}
              />
              <div
                className="bg-emerald-500 transition-all"
                style={{
                  width: `${Math.min(
                    weeklyCoverage.focusPercent,
                    Math.max(0, 100 - weeklyCoverage.meetingPercent),
                  )}%`,
                }}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">
                <strong className="text-foreground">{minutesToHours(weeklyCoverage.meetingMinutes)}</strong> meetings
              </span>
              {meetingTrend && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                        meetingTrend.kind === 'up' && 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
                        meetingTrend.kind === 'down' && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
                        meetingTrend.kind === 'same' && 'bg-muted text-muted-foreground',
                      )}
                    >
                      {meetingTrend.kind === 'same' && <Minus className="h-3 w-3" />}
                      {meetingTrend.kind === 'up' && <TrendingUp className="h-3 w-3" />}
                      {meetingTrend.kind === 'down' && <TrendingDown className="h-3 w-3" />}
                      {meetingTrend.kind === 'same'
                        ? '≈ last week'
                        : `${meetingTrend.hours}h vs last week`}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {meetingTrend.kind === 'same'
                      ? 'About the same as last week'
                      : `${meetingTrend.hours}h ${meetingTrend.kind === 'up' ? 'more' : 'less'} than last week (${minutesToHours(lastWeekCoverage.meetingMinutes)})`}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              <strong className="text-foreground">{minutesToHours(weeklyCoverage.focusMinutes)}</strong> focus
            </span>
            <span
              className={cn(
                'text-xs',
                weeklyCoverage.overloaded
                  ? 'font-semibold text-rose-600 dark:text-rose-400'
                  : 'text-muted-foreground',
              )}
            >
              {weeklyCoverage.overloaded ? (
                'Week over baseline'
              ) : (
                <>
                  <strong className="text-foreground">{minutesToHours(weeklyCoverage.freeMinutes)}</strong> unallocated
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Banners / alerts */}
      {connectionBanner && (
        <Alert variant="success" className="mb-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <AlertDescription>{connectionBanner}</AlertDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setConnectionBanner(null)}>Dismiss</Button>
          </div>
        </Alert>
      )}
      {syncMessage && (
        <Alert variant={syncMessage.type === 'success' ? 'success' : 'error'} className="mb-2">
          <div className="flex items-start justify-between gap-3">
            <AlertDescription className="flex-1">{syncMessage.text}</AlertDescription>
            <Button variant="ghost" size="sm" onClick={() => setSyncMessage(null)}>Dismiss</Button>
          </div>
        </Alert>
      )}
      {createEventMessage && (
        <Alert variant={createEventMessage.type === 'success' ? 'success' : 'error'} className="mb-2">
          <div className="flex items-start justify-between gap-3">
            <AlertDescription className="flex-1">{createEventMessage.text}</AlertDescription>
            <Button variant="ghost" size="sm" onClick={() => setCreateEventMessage(null)}>Dismiss</Button>
          </div>
        </Alert>
      )}

      {/* Empty state — no calendar connected for the CURRENT view (own
          account, or the selected principal's). Without this the page showed
          a bare empty grid that read as broken rather than unconnected —
          exactly how Rosie interpreted her own (never-connected) account
          after EA views became explicit (2026-08-26). The sources fetch runs
          under the effective user, so the prompt is correct per-view. */}
      {sourcesLoaded && oauthSources.length === 0 && events.length === 0 && (
        <Alert className="mb-2">
          <div className="flex items-center justify-between gap-3">
            <AlertDescription className="flex-1">
              No calendar is connected yet for this view — connect Microsoft 365 or
              Google to see meetings here.
            </AlertDescription>
            <Button size="sm" onClick={() => setDrawerOpen(true)}>
              Connect calendar
            </Button>
          </div>
        </Alert>
      )}

      {/* Calendar — react-big-calendar; CSS lives in calendarStyles.ts */}
      <div className="min-h-0 flex-grow">
        <Calendar<CalendarBigEvent>
          localizer={localizer}
          events={mappedEvents}
          date={currentDate}
          view={currentView}
          onNavigate={setCurrentDate}
          onView={setCurrentView}
          onRangeChange={handleRangeChange}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventPropGetter}
          components={{ event: CalendarEventTile }}
          style={{ height: '100%' }}
          views={[Views.DAY, Views.WEEK, Views.MONTH]}
          toolbar={false}
          popup
          selectable
          onSelectSlot={(slotInfo) => {
            setCreateEventData(prev => ({
              ...prev,
              title: '',
              startTime: slotInfo.start.toISOString().slice(0, 16),
              endTime: slotInfo.end.toISOString().slice(0, 16),
            }));
            setCreateEventOpen(true);
          }}
          scrollToTime={new Date(1970, 0, 1, 7, 0, 0)}
        />
      </div>

      {/* Settings sheet — slides in from the right with calendar source
          connection, source list, and current-view stats. */}
      <Sheet open={drawerOpen} onOpenChange={(o) => !o && setDrawerOpen(false)}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Calendar Settings</SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex flex-col gap-4">
            <ConnectCalendarCard />
            <CalendarSourcesCard />
            <Card>
              <CardContent className="py-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Current View Stats
                </p>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Events</p>
                    <p className="text-lg font-semibold tabular-nums">{events.length}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Meeting Time</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {Math.round(meetingMinutes / 60 * 10) / 10}h
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Priority Meetings</p>
                    <p className="text-lg font-semibold tabular-nums text-rose-600 dark:text-rose-400">
                      {priorityMeetingCount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </SheetContent>
      </Sheet>

      {/* Event Detail Dialog — shared component, stays as-is. */}
      <CalendarEventDetail
        event={selectedEvent}
        open={eventDetailOpen}
        onClose={() => {
          setEventDetailOpen(false);
          setSelectedEvent(null);
        }}
        onDriverLinked={loadCurrentRange}
        drivers={activeDrivers}
      />

      {/* Create Event dialog — writes a new event to the selected OAuth
          calendar source. */}
      <Dialog open={createEventOpen} onOpenChange={(o) => !o && setCreateEventOpen(false)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Calendar Event</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ev-source">Calendar</Label>
              <Select
                value={createEventData.sourceId}
                onValueChange={(v) => setCreateEventData(prev => ({ ...prev, sourceId: v }))}
              >
                <SelectTrigger id="ev-source"><SelectValue placeholder="Choose a calendar" /></SelectTrigger>
                <SelectContent>
                  {oauthSources.map(source => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name} ({source.sourceType === 'Microsoft365' ? 'Microsoft 365' : 'Google'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ev-title">Title</Label>
              <Input
                id="ev-title"
                value={createEventData.title}
                onChange={(e) => setCreateEventData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Meeting title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ev-start">Start</Label>
                <Input
                  id="ev-start"
                  type="datetime-local"
                  value={createEventData.startTime}
                  onChange={(e) => setCreateEventData(prev => ({ ...prev, startTime: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ev-end">End</Label>
                <Input
                  id="ev-end"
                  type="datetime-local"
                  value={createEventData.endTime}
                  onChange={(e) => setCreateEventData(prev => ({ ...prev, endTime: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ev-location">Location</Label>
              <Input
                id="ev-location"
                value={createEventData.location}
                onChange={(e) => setCreateEventData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Office, Zoom link, etc."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ev-description">Description</Label>
              <Textarea
                id="ev-description"
                rows={3}
                value={createEventData.description}
                onChange={(e) => setCreateEventData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Event description..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ev-type">Event Type</Label>
              <Select
                value={createEventData.eventType}
                onValueChange={(v) => setCreateEventData(prev => ({ ...prev, eventType: v }))}
              >
                <SelectTrigger id="ev-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Meeting">Meeting</SelectItem>
                  <SelectItem value="FocusTime">Focus Time</SelectItem>
                  <SelectItem value="Travel">Travel</SelectItem>
                  <SelectItem value="Reservation">Reservation</SelectItem>
                  <SelectItem value="Personal">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ev-attendees">Attendees</Label>
              <Input
                id="ev-attendees"
                value={createEventData.attendees}
                onChange={(e) => setCreateEventData(prev => ({ ...prev, attendees: e.target.value }))}
                placeholder="email1@example.com, email2@example.com"
              />
              <p className="text-xs text-muted-foreground">Comma-separated email addresses</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateEventOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateEvent}
              disabled={creatingEvent || !createEventData.title.trim() || !createEventData.startTime || !createEventData.endTime || !createEventData.sourceId}
            >
              {creatingEvent ? 'Creating...' : 'Create Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restaurant Search Dialog — shared component, stays as-is. */}
      <RestaurantSearchDialog
        open={restaurantDialogOpen}
        onClose={() => setRestaurantDialogOpen(false)}
        onSelect={handleRestaurantSelect}
      />
    </div>
  );
}
